/**
 * A function to create a log object for the 'uploadFileAppsFileUploaded' event (evid: 300)
 *
 * Event description: after trying to load a file in dropbox app. the filed message will show success or fail
 *
 * @param {uploadFileAppsFileUploadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} file_names - file_names (json)
 * @property {string} size - file_size_total
 * @property {string} email - sender_email
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {Url} go_to_my_folder_link - go_to_my_folder_link
 * @property {Url} go_to_app_link - go_to_app_link
 * @property {string} message - free_text_message (success/fail)
 * @property {string} comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<uploadFileAppsFileUploadedParams>} LogObject<uploadFileAppsFileUploadedParams>
 */
export function uploadFileAppsFileUploaded(params) {
    return { evid: 300, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'clickOnButtonOrLink' event (evid: 301)
 *
 * Event description: Site Visitors click on app's button/link
 *
 * @param {clickOnButtonOrLinkParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {string} description - If product -> product ID ;  If web -> web address ;  If page -> empty
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {number} position - position of link in the instagram post
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - {product / web / page / native / none}
 * @return {LogObject<clickOnButtonOrLinkParams>} LogObject<clickOnButtonOrLinkParams>
 */
export function clickOnButtonOrLink(params) {
    return { evid: 301, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'statusChange' event (evid: 302)
 *
 * Event description: app change status
 *
 * @param {statusChangeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} old_status - {connected, disconnected, activated...}
 * @property {GUID} biToken - msid
 * @property {string} description - description
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} new_status - {connected, disconnected, activated...}
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - {connect_account, feature_activation...}
 * @return {LogObject<statusChangeParams>} LogObject<statusChangeParams>
 */
export function statusChange(params) {
    return { evid: 302, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'messageDisplayed' event (evid: 303)
 *
 * Event description: message displayed to site visitor
 *
 * @param {messageDisplayedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {string} msg_type - {error, notification}
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} msg_desc - {welcome, finish_set_up, 'name already exists', 'API not available'...}
 * @property {string} comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<messageDisplayedParams>} LogObject<messageDisplayedParams>
 */
export function messageDisplayed(params) {
    return { evid: 303, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'instagramViewPost' event (evid: 304)
 *
 * Event description: event is triggered when UoU is entering an instagram post
 *
 * @param {instagramViewPostParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} wixlabs_bool_2 - link2 has image (not place holder) = true / false
 * @property {boolean} wixlabs_bool_3 - link3 has image (not place holder) = true / false
 * @property {string} product_1 - Product id 1 (if link1 = product)
 * @property {string} app_type - app type
 * @property {string} link_1 - shows the type of link (product / web / page / native / none)
 * @property {GUID} biToken - msid
 * @property {string} link_2 - shows the type of link (product / web / page / native / none)
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} account_type - account type
 * @property {boolean} wixlabs_bool_1 - link1 has image (not place holder) = true / false
 * @property {string} link_3 - shows the type of link (product / web / page / native / none)
 * @property {string} product_3 - Product id 3 (if link3 = product)
 * @property {string} product_2 - Product id 2 (if link2 = product)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - photo / video / igtv
 * @property {string} gallery_type - posts_feed  / hashtag_feed
 * @return {LogObject<instagramViewPostParams>} LogObject<instagramViewPostParams>
 */
export function instagramViewPost(params) {
    return { evid: 304, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'viewDeviation' event (evid: 305)
 *
 * Event description: event is triggered when UoU
 *
 * @param {viewDeviationParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {boolean} buy_button - is displaying 'buy' button
 * @property {string} comp_id - reflects instance_id in Editor events
 * @property {GUID} app_id - app_id
 * @property {string} type - desktop / mobile
 * @return {LogObject<viewDeviationParams>} LogObject<viewDeviationParams>
 */
export function viewDeviation(params) {
    return { evid: 305, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'donationWasDone' event (evid: 306)
 *
 * Event description: When user clicks on "donate" button
 *
 * @param {donationWasDoneParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {number} wixlabs_num_3 - Days Left
 * @property {GUID} visitor_id - visitor_id
 * @property {number} wixlabs_num_2 - Donated
 * @property {number} position - send the "amount"
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} wixlabs_num_1 - Contributors
 * @property {string} type - Mobile / Desktop
 * @return {LogObject<donationWasDoneParams>} LogObject<donationWasDoneParams>
 */
export function donationWasDone(params) {
    return { evid: 306, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'instagramFeedAppLoads' event (evid: 307)
 *
 * Event description: Send when the app loads.
 *
 * @param {instagramFeedAppLoadsParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} source - source
 * @property {string} app_type - app type
 * @property {GUID} app_site_id - instance id
 * @property {GUID} biToken - msid
 * @property {string} description - docker1 / docker2
 * @property {number} followers_count - num of followers in Instagram
 * @property {GUID} visitor_id - visitor_id
 * @property {string} account_type - account type
 * @property {string} user_id - instagram user id (if not connected, null)
 * @property {string} api_description - api error description
 * @property {number} num_of_columns - number of photos in each row in desktop (Editor and ADI)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} layout - desktop layout - grid / collage / slider (Editor and ADI)
 * @property {string} type - type of instagram account (business / private / demo - didn't connect)
 * @property {number} num_of_rows - number of rows in desktop (Editor and ADI)
 * @return {LogObject<instagramFeedAppLoadsParams>} LogObject<instagramFeedAppLoadsParams>
 */
export function instagramFeedAppLoads(params) {
    return { evid: 307, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'shareYourMobilePlaceClickOnButton' event (evid: 310)
 *
 * Event description: When UoU clicks on one of the buttons in the app.
 *
 * @param {shareYourMobilePlaceClickOnButtonParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} country_number - The country code of the phone number
 * @property {string} button_description - send / send again / app store / google play
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_form - box / strip
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - spaces / fit
 * @return {LogObject<shareYourMobilePlaceClickOnButtonParams>} LogObject<shareYourMobilePlaceClickOnButtonParams>
 */
export function shareYourMobilePlaceClickOnButton(params) {
    return { evid: 310, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'shareYourMobilePlaceError' event (evid: 311)
 *
 * Event description: UoU gets an error.
 *
 * @param {shareYourMobilePlaceErrorParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} error_type - invalid number / API
 * @property {string} app_form - box / strip
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - spaces / fit
 * @return {LogObject<shareYourMobilePlaceErrorParams>} LogObject<shareYourMobilePlaceErrorParams>
 */
export function shareYourMobilePlaceError(params) {
    return { evid: 311, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sympAppLoaded' event (evid: 312)
 *
 * Event description: event triggered when the app is viewed in the live site.
 *
 * @param {sympAppLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} app_form - box / strip
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - which mobile app - spaces / fit
 * @return {LogObject<sympAppLoadedParams>} LogObject<sympAppLoadedParams>
 */
export function sympAppLoaded(params) {
    return { evid: 312, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'hoveringOverPost' event (evid: 313)
 *
 * Event description: Site Visitors hover over post
 *
 * @param {hoveringOverPostParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} app_type - app type
 * @property {GUID} biToken - msid
 * @property {string} description - description
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} account_type - account type
 * @property {number} position - the position of the post in the feed
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - {product / web / page / native / none}
 * @property {string} gallery_type - posts_feed / hashtag_feed
 * @return {LogObject<hoveringOverPostParams>} LogObject<hoveringOverPostParams>
 */
export function hoveringOverPost(params) {
    return { evid: 313, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'instagramFeedError' event (evid: 314)
 *
 * Event description: Instagram Errors in Live Site
 *
 * @param {instagramFeedErrorParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} app_type - app type
 * @property {GUID} biToken - msid
 * @property {string} description - error description
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} account_type - account type
 * @property {string} api_description - api error description
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - error type
 * @return {LogObject<instagramFeedErrorParams>} LogObject<instagramFeedErrorParams>
 */
export function instagramFeedError(params) {
    return { evid: 314, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'instagramFeedViewStory' event (evid: 315)
 *
 * Event description: event for each story separately
 *
 * @param {instagramFeedViewStoryParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} app_type - app type
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - get this ID from the signed instance using Wix.Utils.getInstanceValue('aid')
 * @property {string} account_type - account type
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - video / image
 * @return {LogObject<instagramFeedViewStoryParams>} LogObject<instagramFeedViewStoryParams>
 */
export function instagramFeedViewStory(params) {
    return { evid: 315, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'instagramFeedAppLoadsStartLoading' event (evid: 317)
 *
 * Event description: Send when the app loads.
 *
 * @param {instagramFeedAppLoadsStartLoadingParams} params - The parameters for this event
 * @property {GUID} instance_id - instance id
 * @property {GUID} app_site_id - deprecated
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<instagramFeedAppLoadsStartLoadingParams>} LogObject<instagramFeedAppLoadsStartLoadingParams>
 */
export function instagramFeedAppLoadsStartLoading(params) {
    return { evid: 317, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'etsy20SortBy' event (evid: 350)
 *
 * Event description: User or UoU sort by
 *
 * @param {etsy20SortByParams} params - The parameters for this event
 * @property {GUID} instance_id - comp id
 * @property {GUID} app_site_id - instance id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} sort_by - Most Recent / Relevance / Highest Price / Loweat Price
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<etsy20SortByParams>} LogObject<etsy20SortByParams>
 */
export function etsy20SortBy(params) {
    return { evid: 350, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareNewFileClick' event (evid: 421)
 *
 * Event description: when the user clicks on 'new file' button
 *
 * @param {fileShareNewFileClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false - is this the first click
 * @property {string} origin - null or upload_button
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareNewFileClickParams>} LogObject<fileShareNewFileClickParams>
 */
export function fileShareNewFileClick(params) {
    return { evid: 421, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareNewFolderClick' event (evid: 422)
 *
 * Event description: when the user clicks on 'new folder' button
 *
 * @param {fileShareNewFolderClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false - is this the first click
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareNewFolderClickParams>} LogObject<fileShareNewFolderClickParams>
 */
export function fileShareNewFolderClick(params) {
    return { evid: 422, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareCameraClick' event (evid: 423)
 *
 * Event description: when the user clicks on 'camera' button
 *
 * @param {fileShareCameraClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false - is this the first click
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareCameraClickParams>} LogObject<fileShareCameraClickParams>
 */
export function fileShareCameraClick(params) {
    return { evid: 423, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareNewFileCreated' event (evid: 424)
 *
 * Event description: when a new file created
 *
 * @param {fileShareNewFileCreatedParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} action_id - action_id of the request. to join try and success events
 * @property {GUID} file_id - unique id for each file
 * @property {boolean} is_camera - true/false - is the file uploaded via camera
 * @property {string} file_name - file_name
 * @property {GUID} biToken - msid
 * @property {string} description - load, create
 * @property {number} file_size - file_size
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false - is this the first click
 * @property {string} origin - origin
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {GUID} parent_folder_id - parent_folder_id
 * @property {string} level - level
 * @return {LogObject<fileShareNewFileCreatedParams>} LogObject<fileShareNewFileCreatedParams>
 */
export function fileShareNewFileCreated(params) {
    return { evid: 424, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareNewFolderCreated' event (evid: 425)
 *
 * Event description: when a new folder created
 *
 * @param {fileShareNewFolderCreatedParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} action_id - action_id of the request. to join try and success events
 * @property {boolean} is_camera - true/false - is the file uploaded via camera
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false - is this the first click
 * @property {string} origin - from where the folder creates: 'home' if opened from main window / 'move' if opened from the move dialog
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {string} folder_name - folder_name
 * @property {GUID} parent_folder_id - parent_folder_id
 * @property {string} level - level
 * @return {LogObject<fileShareNewFolderCreatedParams>} LogObject<fileShareNewFolderCreatedParams>
 */
export function fileShareNewFolderCreated(params) {
    return { evid: 425, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareFileMenuClick' event (evid: 426)
 *
 * Event description: click on file menu
 *
 * @param {fileShareFileMenuClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {string} button_description - download, share, rename, move, delete, permissions
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {number} num_of_files_selected - the number of files selected
 * @property {boolean} is_file_owner - True/False
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} origin - multi selection / single item
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file = visitor id = aid
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareFileMenuClickParams>} LogObject<fileShareFileMenuClickParams>
 */
export function fileShareFileMenuClick(params) {
    return { evid: 426, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareDeleteFile' event (evid: 427)
 *
 * Event description: click on file menu
 *
 * @param {fileShareDeleteFileParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} action_id - action_id of the request. to join try and success events
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {string} description - click, deleted
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {number} num_of_files_selected - the number of files selected
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} origin - multi selection / single item
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareDeleteFileParams>} LogObject<fileShareDeleteFileParams>
 */
export function fileShareDeleteFile(params) {
    return { evid: 427, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareMoveFileClick' event (evid: 428)
 *
 * Event description: click on file menu
 *
 * @param {fileShareMoveFileClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} action_id - action_id of the request. to join try and success events
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {string} description - click, moved
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {number} num_of_files_selected - the number of files selected
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} origin - multi selection / single item
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareMoveFileClickParams>} LogObject<fileShareMoveFileClickParams>
 */
export function fileShareMoveFileClick(params) {
    return { evid: 428, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareRenameFile' event (evid: 429)
 *
 * Event description: view file
 *
 * @param {fileShareRenameFileParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} action_id - action_id of the request. to join try and success events
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareRenameFileParams>} LogObject<fileShareRenameFileParams>
 */
export function fileShareRenameFile(params) {
    return { evid: 429, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareSortBy' event (evid: 430)
 *
 * Event description: sort by :
 * date, name, type, owner, stars, views
 *
 * @param {fileShareSortByParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {string} button_description - date, name, type, owner, stars, views
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_first - true/false
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<fileShareSortByParams>} LogObject<fileShareSortByParams>
 */
export function fileShareSortBy(params) {
    return { evid: 430, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareSearch' event (evid: 431)
 *
 * Event description: search
 *
 * @param {fileShareSearchParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {string} search_text - search_text
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} results_count - number of results
 * @property {string} origin - home, folder
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} folder_name - folder_name
 * @return {LogObject<fileShareSearchParams>} LogObject<fileShareSearchParams>
 */
export function fileShareSearch(params) {
    return { evid: 431, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareViewFile' event (evid: 432)
 *
 * Event description: view file / open folder
 *
 * @param {fileShareViewFileParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_restricted - is_restricted
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareViewFileParams>} LogObject<fileShareViewFileParams>
 */
export function fileShareViewFile(params) {
    return { evid: 432, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareFavoriteClick' event (evid: 433)
 *
 * Event description: Favorite click
 *
 * @param {fileShareFavoriteClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareFavoriteClickParams>} LogObject<fileShareFavoriteClickParams>
 */
export function fileShareFavoriteClick(params) {
    return { evid: 433, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareViewAppPage' event (evid: 436)
 *
 * Event description: event triggered when the app is viewed in the live site
 *
 * @param {fileShareViewAppPageParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} view_type - widget / page app
 * @property {number} folder_id - only for widget = the id of the folder that the user chose to show
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} widget_folder_id - only for widget = the id of the folder that the user chose to show
 * @property {string} folder_name - only for widget = the name of the folder that the user chose to show
 * @return {LogObject<fileShareViewAppPageParams>} LogObject<fileShareViewAppPageParams>
 */
export function fileShareViewAppPage(params) {
    return { evid: 436, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareDownloadFile' event (evid: 437)
 *
 * Event description: the event is triggered when clicking download
 *
 * @param {fileShareDownloadFileParams} params - The parameters for this event
 * @property {GUID} app_site_id - instance_id
 * @property {GUID} instance_id - comp_id
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileShareDownloadFileParams>} LogObject<fileShareDownloadFileParams>
 */
export function fileShareDownloadFile(params) {
    return { evid: 437, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareReport' event (evid: 438)
 *
 * Event description: User or UoU report on a file / folder
 *
 * @param {fileShareReportParams} params - The parameters for this event
 * @property {GUID} instance_id - comp_id
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {GUID} biToken - msid
 * @property {number} file_size - size of the file
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - instance_id
 * @property {GUID} app_id - app_id
 * @property {string} file_type - type of the file
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @property {string} report_reason - one of the four reasons of report
 * @return {LogObject<fileShareReportParams>} LogObject<fileShareReportParams>
 */
export function fileShareReport(params) {
    return { evid: 438, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareOpenInLibraryClick' event (evid: 439)
 *
 * Event description: open in library click in members area
 *
 * @param {fileShareOpenInLibraryClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} file_id - file_id
 * @property {string} file_name - file_name
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {number} file_size - file_size
 * @property {number} favorites_count - number of favorites
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} file_owner_id - visitor id of the file owner
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} file_owner - Site Member who upload the file
 * @property {string} file_type - file_type
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareOpenInLibraryClickParams>} LogObject<fileShareOpenInLibraryClickParams>
 */
export function fileShareOpenInLibraryClick(params) {
    return { evid: 439, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareUploadsTabClick' event (evid: 440)
 *
 * Event description: uploads tab click in members area
 *
 * @param {fileShareUploadsTabClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareUploadsTabClickParams>} LogObject<fileShareUploadsTabClickParams>
 */
export function fileShareUploadsTabClick(params) {
    return { evid: 440, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareFavoritesTabClick' event (evid: 446)
 *
 * Event description: My Favorites tab click in members area
 *
 * @param {fileShareFavoritesTabClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @return {LogObject<fileShareFavoritesTabClickParams>} LogObject<fileShareFavoritesTabClickParams>
 */
export function fileShareFavoritesTabClick(params) {
    return { evid: 446, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsSavedStep1' event (evid: 450)
 *
 * Event description: when permissions saved in step 1 (first window)
 *
 * @param {fileSharePermissionsSavedStep1Params} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} alert_notification - if saved and the alert of conflicting permissions is still still shown
 * @property {number} specific_members_count - specific_members_count
 * @property {number} roles_count - roles_count
 * @property {number} plans_count - plans_count
 * @property {GUID} file_id - file_id / folder_id
 * @property {number} view_count - view_count
 * @property {GUID} biToken - msid
 * @property {number} badges_count - badges_count
 * @property {number} manage_count - manage_count
 * @property {string} isHidden - true / false
 * @property {GUID} visitor_id - visitor_id
 * @property {number} all_members - 0/1
 * @property {number} upload_count - upload_count
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} total_permissions_count - total_permissions_count
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @property {number} site_visitors - 0/1
 * @property {boolean} isFromAiAssistant - if it was triggered by AI
 * @return {LogObject<fileSharePermissionsSavedStep1Params>} LogObject<fileSharePermissionsSavedStep1Params>
 */
export function fileSharePermissionsSavedStep1(params) {
    return { evid: 450, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsSavedStep2' event (evid: 451)
 *
 * Event description: when permissions saved in step 2 (second window when then the members can be notified)
 *
 * @param {fileSharePermissionsSavedStep2Params} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} file_id - file_id / folder_id
 * @property {GUID} biToken - msid
 * @property {string} notification - 3 options: didnt_notify, notify_with_massage, notify_without_massage (empty text box)
 * @property {GUID} visitor_id - visitor_id
 * @property {number} tags_count - (how many permissions groups)
 * @property {string} permission_type - view / upload / manage
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileSharePermissionsSavedStep2Params>} LogObject<fileSharePermissionsSavedStep2Params>
 */
export function fileSharePermissionsSavedStep2(params) {
    return { evid: 451, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsSignAdded' event (evid: 452)
 *
 * Event description: when the lock or dollar sign is added to the restricted folder
 *
 * @param {fileSharePermissionsSignAddedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} file_id - file_id / folder_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {string} type - lock / dollar
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileSharePermissionsSignAddedParams>} LogObject<fileSharePermissionsSignAddedParams>
 */
export function fileSharePermissionsSignAdded(params) {
    return { evid: 452, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsSignRemoved' event (evid: 454)
 *
 * Event description: when the lock or dollar sign is added to the restricted folder
 *
 * @param {fileSharePermissionsSignRemovedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} file_id - file_id / folder_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {string} type - lock / dollar
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileSharePermissionsSignRemovedParams>} LogObject<fileSharePermissionsSignRemovedParams>
 */
export function fileSharePermissionsSignRemoved(params) {
    return { evid: 454, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsAccessWindowOpened' event (evid: 455)
 *
 * Event description: when clicking on the restricted folder and the get access window opens
 *
 * @param {fileSharePermissionsAccessWindowOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} file_id - file_id / folder_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {string} type - lock / dollar
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileSharePermissionsAccessWindowOpenedParams>} LogObject<fileSharePermissionsAccessWindowOpenedParams>
 */
export function fileSharePermissionsAccessWindowOpened(params) {
    return { evid: 455, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileSharePermissionsGetAccessClick' event (evid: 456)
 *
 * Event description: when clicking on the button in the get access window when the visitor don't have permissions to the folder
 *
 * @param {fileSharePermissionsGetAccessClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} button_description - send_request /switch_account / see_plans / login
 * @property {GUID} file_id - file_id / folder_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} owner - Owner (who does the ‘action’) = Role = member/admin/moderator/<custom_string>
 * @property {string} type - lock / dollar
 * @property {boolean} is_folder - is_folder
 * @return {LogObject<fileSharePermissionsGetAccessClickParams>} LogObject<fileSharePermissionsGetAccessClickParams>
 */
export function fileSharePermissionsGetAccessClick(params) {
    return { evid: 456, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryAddPhotoClick' event (evid: 521)
 *
 * Event description: User or UoU add a photo.
 *
 * @param {sharedGalleryAddPhotoClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {string} button_description - take a photo / choose from device
 * @property {string} button_type - top page button  / empty state inside gallery button
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - album_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - image / video
 * @return {LogObject<sharedGalleryAddPhotoClickParams>} LogObject<sharedGalleryAddPhotoClickParams>
 */
export function sharedGalleryAddPhotoClick(params) {
    return { evid: 521, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryNewPhotoAdded' event (evid: 522)
 *
 * Event description: New photo added to the app.
 *
 * @param {sharedGalleryNewPhotoAddedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {string} photo_type - the type of the photo (by the end of the photo name)
 * @property {GUID} action_id - unique id for action to join the actions on the client and the server
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {number} photo_size - the size of the photo
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryNewPhotoAddedParams>} LogObject<sharedGalleryNewPhotoAddedParams>
 */
export function sharedGalleryNewPhotoAdded(params) {
    return { evid: 522, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGallerySortBy' event (evid: 523)
 *
 * Event description: User or UoU sort by.
 *
 * @param {sharedGallerySortByParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - album_id
 * @property {string} sort_by - Most Recent / Most tagged members / Most Liked / Most Comments
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGallerySortByParams>} LogObject<sharedGallerySortByParams>
 */
export function sharedGallerySortBy(params) {
    return { evid: 523, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryExpandPhoto' event (evid: 524)
 *
 * Event description: User or UoU click on a photo.
 *
 * @param {sharedGalleryExpandPhotoParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {string} photo_type - the type of the photo (by the end of the photo name)
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {number} views_count - number of views
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {number} photo_size - the size of the photo
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryExpandPhotoParams>} LogObject<sharedGalleryExpandPhotoParams>
 */
export function sharedGalleryExpandPhoto(params) {
    return { evid: 524, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryTagMembers' event (evid: 525)
 *
 * Event description: User or UoU tag members on a photo
 *
 * @param {sharedGalleryTagMembersParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} number_of_tags - number of members that tagged in the photo
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryTagMembersParams>} LogObject<sharedGalleryTagMembersParams>
 */
export function sharedGalleryTagMembers(params) {
    return { evid: 525, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryDescribePhoto' event (evid: 526)
 *
 * Event description: User or UoU add a description to the photo.
 *
 * @param {sharedGalleryDescribePhotoParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {number} hashtag_count - hashtag
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {number} mention_count - mention
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} link_count - links
 * @property {string} photo_name - The name of the file
 * @property {number} emoji_count - emoji
 * @return {LogObject<sharedGalleryDescribePhotoParams>} LogObject<sharedGalleryDescribePhotoParams>
 */
export function sharedGalleryDescribePhoto(params) {
    return { evid: 526, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryLike' event (evid: 527)
 *
 * Event description: User or UoU click on love a photo
 *
 * @param {sharedGalleryLikeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {boolean} is_new_like - True if it's a like and False if the click delete the like.
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} likes_count - number of likes
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryLikeParams>} LogObject<sharedGalleryLikeParams>
 */
export function sharedGalleryLike(params) {
    return { evid: 527, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryShare' event (evid: 528)
 *
 * Event description: User or UoU share a photo.
 *
 * @param {sharedGalleryShareParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryShareParams>} LogObject<sharedGalleryShareParams>
 */
export function sharedGalleryShare(params) {
    return { evid: 528, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryReport' event (evid: 529)
 *
 * Event description: User or UoU report on a photo.
 *
 * @param {sharedGalleryReportParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} mode - expand / gallery (from which mode the photo was reported)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @property {string} report_reason - the reason of the report
 * @return {LogObject<sharedGalleryReportParams>} LogObject<sharedGalleryReportParams>
 */
export function sharedGalleryReport(params) {
    return { evid: 529, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryDelete' event (evid: 530)
 *
 * Event description: User or UoU delete a photo.
 *
 * @param {sharedGalleryDeleteParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {number} num_of_images_selected - num_of_images_selected
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} mode - expand / gallery (from which mode the photo was reported)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @property {string} action_type - select all / multi select / single item
 * @return {LogObject<sharedGalleryDeleteParams>} LogObject<sharedGalleryDeleteParams>
 */
export function sharedGalleryDelete(params) {
    return { evid: 530, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryPhotoMenuClick' event (evid: 531)
 *
 * Event description: User or UoU click on the photo menu.
 *
 * @param {sharedGalleryPhotoMenuClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} mode - expand / gallery (from which mode the photo was reported)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryPhotoMenuClickParams>} LogObject<sharedGalleryPhotoMenuClickParams>
 */
export function sharedGalleryPhotoMenuClick(params) {
    return { evid: 531, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryDownloadPhoto' event (evid: 532)
 *
 * Event description: User or UoU download a photo.
 *
 * @param {sharedGalleryDownloadPhotoParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {number} num_of_images_selected - for multi select
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} mode - expand / gallery / members area
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @property {string} action_type - select all / multi select / single item
 * @return {LogObject<sharedGalleryDownloadPhotoParams>} LogObject<sharedGalleryDownloadPhotoParams>
 */
export function sharedGalleryDownloadPhoto(params) {
    return { evid: 532, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryViewAppPage' event (evid: 535)
 *
 * Event description: The app is viewed in the live site
 *
 * @param {sharedGalleryViewAppPageParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {number} albums_count - only in albums view
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - only in gallery view
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - gallery view / album view
 * @return {LogObject<sharedGalleryViewAppPageParams>} LogObject<sharedGalleryViewAppPageParams>
 */
export function sharedGalleryViewAppPage(params) {
    return { evid: 535, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryCreateAlbumClick' event (evid: 540)
 *
 * Event description: User or UoU create an album.
 *
 * @param {sharedGalleryCreateAlbumClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - gallery view / albums view
 * @return {LogObject<sharedGalleryCreateAlbumClickParams>} LogObject<sharedGalleryCreateAlbumClickParams>
 */
export function sharedGalleryCreateAlbumClick(params) {
    return { evid: 540, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryNewAlbumCreated' event (evid: 541)
 *
 * Event description: New album created.
 *
 * @param {sharedGalleryNewAlbumCreatedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {boolean} is_description_added - is_description_added
 * @property {boolean} is_edited - false = if it's the first time the album was created, true = if edited and saved
 * @property {GUID} action_id - action_id to join events on server and client side
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {boolean} is_cover_image_added - is_cover_image_added
 * @property {string} cover_image_id - photo_id of the cover image
 * @property {string} upload_photos - who can upload and tag: admins / all members / <custom_string>
 * @return {LogObject<sharedGalleryNewAlbumCreatedParams>} LogObject<sharedGalleryNewAlbumCreatedParams>
 */
export function sharedGalleryNewAlbumCreated(params) {
    return { evid: 541, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryMoveItem' event (evid: 542)
 *
 * Event description: User or UoU move item to different album.
 *
 * @param {sharedGalleryMoveItemParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - new album id
 * @property {string} mode - expand / gallery (from which mode the photo was transferred)
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @property {string} old_album_id - old album id
 * @return {LogObject<sharedGalleryMoveItemParams>} LogObject<sharedGalleryMoveItemParams>
 */
export function sharedGalleryMoveItem(params) {
    return { evid: 542, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryShareAlbum' event (evid: 543)
 *
 * Event description: user or UoU shared an album
 *
 * @param {sharedGalleryShareAlbumParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {string} button_description - Share via social / copy link / invite to album
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGalleryShareAlbumParams>} LogObject<sharedGalleryShareAlbumParams>
 */
export function sharedGalleryShareAlbum(params) {
    return { evid: 543, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryDeleteAlbum' event (evid: 544)
 *
 * Event description: user or UoU delete an album
 *
 * @param {sharedGalleryDeleteAlbumParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {number} num_of_images_deleted - num_of_images_deleted
 * @property {GUID} action_id - action_id to join events on server and client side
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGalleryDeleteAlbumParams>} LogObject<sharedGalleryDeleteAlbumParams>
 */
export function sharedGalleryDeleteAlbum(params) {
    return { evid: 544, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGallerySortByAlbum' event (evid: 545)
 *
 * Event description: user or UoU sort by (album)
 *
 * @param {sharedGallerySortByAlbumParams} params - The parameters for this event
 * @property {GUID} instance_id - comp_id
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} sort_by - Recent Activity / Album Name (A-Z) / Most tagged members / Most Comments
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGallerySortByAlbumParams>} LogObject<sharedGallerySortByAlbumParams>
 */
export function sharedGallerySortByAlbum(params) {
    return { evid: 545, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryAlbumMenuClick' event (evid: 546)
 *
 * Event description: User or UoU click on the album menu.
 *
 * @param {sharedGalleryAlbumMenuClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {string} button_description - edit / download / share / delete
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - gallery view / albums view
 * @return {LogObject<sharedGalleryAlbumMenuClickParams>} LogObject<sharedGalleryAlbumMenuClickParams>
 */
export function sharedGalleryAlbumMenuClick(params) {
    return { evid: 546, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryCommentReplyAdded' event (evid: 548)
 *
 * Event description: User or UoU post a comment / reply.
 *
 * @param {sharedGalleryCommentReplyAddedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - photo_id of the comment
 * @property {string} has_media - Camera / Media Gallery / GIF
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} album_id - album_id of the comment
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment - (for reply)
 * @return {LogObject<sharedGalleryCommentReplyAddedParams>} LogObject<sharedGalleryCommentReplyAddedParams>
 */
export function sharedGalleryCommentReplyAdded(params) {
    return { evid: 548, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryLikeComment' event (evid: 549)
 *
 * Event description: User or UoU like a comment
 *
 * @param {sharedGalleryLikeCommentParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} role - role
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - photo_id of the comment
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} album_id - album_id of the comment
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment - (for reply)
 * @return {LogObject<sharedGalleryLikeCommentParams>} LogObject<sharedGalleryLikeCommentParams>
 */
export function sharedGalleryLikeComment(params) {
    return { evid: 549, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryOpenAlbumClick' event (evid: 551)
 *
 * Event description: When clicking on album and open it
 *
 * @param {sharedGalleryOpenAlbumClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} origin - albums_list / my_albums_carousel
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGalleryOpenAlbumClickParams>} LogObject<sharedGalleryOpenAlbumClickParams>
 */
export function sharedGalleryOpenAlbumClick(params) {
    return { evid: 551, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryDownloadAlbum' event (evid: 552)
 *
 * Event description: user or UoU download an album
 *
 * @param {sharedGalleryDownloadAlbumParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - comp_id
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_name - The name of the album
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<sharedGalleryDownloadAlbumParams>} LogObject<sharedGalleryDownloadAlbumParams>
 */
export function sharedGalleryDownloadAlbum(params) {
    return { evid: 552, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sharedGalleryAltTextSaved' event (evid: 566)
 *
 * Event description: When adding alt text to a photo.
 *
 * @param {sharedGalleryAltTextSavedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app instance id
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_image - True if it's image and False if it's video
 * @property {string} role - who does the ‘action’ = member/admin/moderator/<custom_string>
 * @property {GUID} biToken - msid
 * @property {GUID} photo_id - unique id for each photo
 * @property {GUID} visitor_id - visitor_id
 * @property {string} album_id - unique id for each album
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} photo_name - The name of the file
 * @return {LogObject<sharedGalleryAltTextSavedParams>} LogObject<sharedGalleryAltTextSavedParams>
 */
export function sharedGalleryAltTextSaved(params) {
    return { evid: 566, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsPost' event (evid: 630)
 *
 * Event description: User or UoU post a comment / reply.
 *
 * @param {commentsPostParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} image_count - number of images in the comment
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {string} has_media - Camera / Media Gallery / GIF.
 * @property {GUID} visitor_id - visitor_id
 * @property {string} permissions - Viewer / Writer / Admin.
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component (post_id. photo_id, etc..))
 * @property {number} gif_count - number of gifs in the comment
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @property {number} video_count - number of videos in the comment
 * @property {number} emoji_count - number of emoji in the comment
 * @return {LogObject<commentsPostParams>} LogObject<commentsPostParams>
 */
export function commentsPost(params) {
    return { evid: 630, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsDelete' event (evid: 631)
 *
 * Event description: User or UoU delete a comment / reply.
 *
 * @param {commentsDeleteParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} by_user - True if deleted by user and False if deleted by admin.
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @return {LogObject<commentsDeleteParams>} LogObject<commentsDeleteParams>
 */
export function commentsDelete(params) {
    return { evid: 631, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsReport' event (evid: 632)
 *
 * Event description: User or UoU report a comment / reply.
 *
 * @param {commentsReportParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @property {string} report_reason - one of the four options given.
 * @return {LogObject<commentsReportParams>} LogObject<commentsReportParams>
 */
export function commentsReport(params) {
    return { evid: 632, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsShare' event (evid: 633)
 *
 * Event description: User or UoU share a comment / reply.
 *
 * @param {commentsShareParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @return {LogObject<commentsShareParams>} LogObject<commentsShareParams>
 */
export function commentsShare(params) {
    return { evid: 633, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsLike' event (evid: 634)
 *
 * Event description: User or UoU like a comment / reply.
 *
 * @param {commentsLikeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @return {LogObject<commentsLikeParams>} LogObject<commentsLikeParams>
 */
export function commentsLike(params) {
    return { evid: 634, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsPreviousComments' event (evid: 635)
 *
 * Event description: show previous comment
 *
 * @param {commentsPreviousCommentsParams} params - The parameters for this event
 * @property {GUID} instance_id - instance id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {number} number_of_comments - number of comments in page
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment - if reply so null.
 * @return {LogObject<commentsPreviousCommentsParams>} LogObject<commentsPreviousCommentsParams>
 */
export function commentsPreviousComments(params) {
    return { evid: 635, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsLoaded' event (evid: 636)
 *
 * Event description: Comments section was loaded.
 *
 * @param {commentsLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} app_site_id - app id of the hosting tpa
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app id
 * @return {LogObject<commentsLoadedParams>} LogObject<commentsLoadedParams>
 */
export function commentsLoaded(params) {
    return { evid: 636, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsMention' event (evid: 637)
 *
 * Event description: User or UoU mention a member in a comment / reply or clicked on it
 *
 * @param {commentsMentionParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - parent_app_id
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @property {string} type - added (comment/reply was published with mention) / clicked (by uou)
 * @property {GUID} mention_visitor_id - The id of the member that was mentioned in the comment / reply
 * @return {LogObject<commentsMentionParams>} LogObject<commentsMentionParams>
 */
export function commentsMention(params) {
    return { evid: 637, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsShowMoreReplies' event (evid: 638)
 *
 * Event description: Comments -> Show More Replies.
 *
 * @param {commentsShowMoreRepliesParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - item_id
 * @property {GUID} action_id - action_id
 * @property {GUID} parent_app_id - parent_app_id
 * @property {number} comment_level - comment_level
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} number_of_comments - ---Deprecated---
 * @property {string} comment_id - comment_id
 * @property {string} entity_id - entity_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - parent_comment
 * @return {LogObject<commentsShowMoreRepliesParams>} LogObject<commentsShowMoreRepliesParams>
 */
export function commentsShowMoreReplies(params) {
    return { evid: 638, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'commentsAddReplyClick' event (evid: 639)
 *
 * Event description: User or UoU wants to add a reply to a comment by clicking on reply button or starting to write in the reply box
 *
 * @param {commentsAddReplyClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} item_id - ---Deprecated---
 * @property {GUID} action_id - unique id for an action of user on the app (simillar on the same client event).
 * @property {GUID} parent_app_id - app_id of the parent app
 * @property {number} comment_level - The level of the comment or the reply
 * @property {GUID} app_site_id - app instance id of the parent app
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comment_id - unique id for every comment.
 * @property {string} entity_id - unique id of the component
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} parent_comment - the id of the parent comment (L1) - if reply so null.
 * @property {string} type - button / reply box
 * @return {LogObject<commentsAddReplyClickParams>} LogObject<commentsAddReplyClickParams>
 */
export function commentsAddReplyClick(params) {
    return { evid: 639, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqAppLoads' event (evid: 860)
 *
 * Event description: Send when the app loads.
 *
 * @param {faqAppLoadsParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} num_of_categories_shown - num_of_categories_shown
 * @property {number} num_of_categories - num_of_categories
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {number} num_of_questions - num_of_questions
 * @property {string} layout_type - type of layout
 * @property {string} columns - columns: one or two
 * @property {string} category_placement - above/beside
 * @return {LogObject<faqAppLoadsParams>} LogObject<faqAppLoadsParams>
 */
export function faqAppLoads(params) {
    return { evid: 860, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqQuestionExpanded' event (evid: 861)
 *
 * Event description: uou clicks on a question to see the answer
 *
 * @param {faqQuestionExpandedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {number} question_order - the position of the question in the category (from top to buttom)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} question_id - question_id
 * @property {string} category_id - category_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {boolean} is_all_categories - is_all_categories
 * @property {string} type - expend / strip / deep link
 * @property {number} category_order - the position of the category (in the categories order)
 * @property {string} question_name - Question that was expanded
 * @property {string} url - site url
 * @return {LogObject<faqQuestionExpandedParams>} LogObject<faqQuestionExpandedParams>
 */
export function faqQuestionExpanded(params) {
    return { evid: 861, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqShareQuestion' event (evid: 862)
 *
 * Event description: FAQ -> share question.
 *
 * @param {faqShareQuestionParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {number} question_order - question_order
 * @property {GUID} visitor_id - visitor_id
 * @property {number} cateogry_order - cateogry_order
 * @property {string} question_id - question_id
 * @property {string} category_id - category_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - type
 * @return {LogObject<faqShareQuestionParams>} LogObject<faqShareQuestionParams>
 */
export function faqShareQuestion(params) {
    return { evid: 862, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqSearch' event (evid: 863)
 *
 * Event description: When uou search in FAQ
 *
 * @param {faqSearchParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<faqSearchParams>} LogObject<faqSearchParams>
 */
export function faqSearch(params) {
    return { evid: 863, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqOpenQuestionToFullScreen' event (evid: 864)
 *
 * Event description: open question to full screen
 *
 * @param {faqOpenQuestionToFullScreenParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {number} question_order - question_order
 * @property {GUID} visitor_id - visitor_id
 * @property {number} cateogry_order - cateogry_order
 * @property {string} question_id - question_id
 * @property {string} category_id - category_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} type - see more / deep link
 * @return {LogObject<faqOpenQuestionToFullScreenParams>} LogObject<faqOpenQuestionToFullScreenParams>
 */
export function faqOpenQuestionToFullScreen(params) {
    return { evid: 864, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqViewAllQuestionsClick' event (evid: 865)
 *
 * Event description: When uou clicks on View all on the mobile app
 *
 * @param {faqViewAllQuestionsClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @return {LogObject<faqViewAllQuestionsClickParams>} LogObject<faqViewAllQuestionsClickParams>
 */
export function faqViewAllQuestionsClick(params) {
    return { evid: 865, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqChangeCategory' event (evid: 866)
 *
 * Event description: uou clicks on a question to see the answer
 *
 * @param {faqChangeCategoryParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} category_id - category_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {boolean} is_all_categories - regular category or all categories
 * @property {number} category_order - category_order
 * @return {LogObject<faqChangeCategoryParams>} LogObject<faqChangeCategoryParams>
 */
export function faqChangeCategory(params) {
    return { evid: 866, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'googleAdsenseAdView' event (evid: 870)
 *
 * Event description: Ad loaded on live site
 *
 * @param {googleAdsenseAdViewParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - user id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_role - owner / member / anonymous
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} ad_id - ad id
 * @property {GUID} site_member_id - site_member_id
 * @property {GUID} msid - msid
 * @return {LogObject<googleAdsenseAdViewParams>} LogObject<googleAdsenseAdViewParams>
 */
export function googleAdsenseAdView(params) {
    return { evid: 870, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'faqAppStartLoading' event (evid: 872)
 *
 * Event description: faq started to load
 *
 * @param {faqAppStartLoadingParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<faqAppStartLoadingParams>} LogObject<faqAppStartLoadingParams>
 */
export function faqAppStartLoading(params) {
    return { evid: 872, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'printingBuilderBuilderLoaded' event (evid: 873)
 *
 * Event description: Printing Builder -> Builder Loaded.
 *
 * @param {printingBuilderBuilderLoadedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} template_settings_id - template_settings_id
 * @property {GUID} entity_id - entity_id
 * @property {string} origin - Only for onlineprograms: edit_certificate / create_certificate
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} builderName - online program certificate / events tickets
 * @return {LogObject<printingBuilderBuilderLoadedParams>} LogObject<printingBuilderBuilderLoadedParams>
 */
export function printingBuilderBuilderLoaded(params) {
    return { evid: 873, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'printingBuilderSidebarClick' event (evid: 874)
 *
 * Event description: Printing Builder -> Sidebar Click
 *
 * @param {printingBuilderSidebarClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} tabName - templates / text / badges / logos / signatures / dividers / frames / backgrounds
 * @property {GUID} template_settings_id - template_settings_id
 * @property {GUID} entity_id - entity_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} builderName - builder_name
 * @return {LogObject<printingBuilderSidebarClickParams>} LogObject<printingBuilderSidebarClickParams>
 */
export function printingBuilderSidebarClick(params) {
    return { evid: 874, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'printingBuilderInsideTabClick' event (evid: 875)
 *
 * Event description: Printing Builder -> Inside Tab Click
 *
 * @param {printingBuilderInsideTabClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} templateName - template name
 * @property {string} tabName - string
 * @property {string} actionType - string
 * @property {GUID} templateId - template id
 * @property {GUID} template_settings_id - template_settings_id
 * @property {GUID} entity_id - entity_id
 * @property {string} comp_id - comp_id
 * @property {string} control - control
 * @property {GUID} app_id - app_id
 * @property {string} property - if control value is not an boolean, property notes which control property was edited
 * @property {string} value - string or null
 * @property {string} builderName - online program certificate
 * @return {LogObject<printingBuilderInsideTabClickParams>} LogObject<printingBuilderInsideTabClickParams>
 */
export function printingBuilderInsideTabClick(params) {
    return { evid: 875, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'printingBuilderClick' event (evid: 876)
 *
 * Event description: Printing Builder -> Click
 *
 * @param {printingBuilderClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} templateName - name or null
 * @property {string} tabName - main / back_modal
 * @property {string} templateId - template id or null
 * @property {GUID} template_settings_id - template_settings_id
 * @property {GUID} entity_id - entity_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} value - string
 * @property {string} builderName - online program certificate
 * @return {LogObject<printingBuilderClickParams>} LogObject<printingBuilderClickParams>
 */
export function printingBuilderClick(params) {
    return { evid: 876, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareSharedFolderAddOnAdded' event (evid: 878)
 *
 * Event description: shared folder add-on added
 *
 * @param {fileShareSharedFolderAddOnAddedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<fileShareSharedFolderAddOnAddedParams>} LogObject<fileShareSharedFolderAddOnAddedParams>
 */
export function fileShareSharedFolderAddOnAdded(params) {
    return { evid: 878, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareUploadButtonState' event (evid: 881)
 *
 * Event description: file share -> upload button state.
 *
 * @param {fileShareUploadButtonStateParams} params - The parameters for this event
 * @property {GUID} app_site_id - guid
 * @property {GUID} instance_id - instance_id
 * @property {string} instanceId - instance_id
 * @property {string} state - error_flow_no_permission / error_flow_private_profile / make_public_profile
 * @property {GUID} biToken - guid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {string} action_type - click_on_action / click_on_button
 * @return {LogObject<fileShareUploadButtonStateParams>} LogObject<fileShareUploadButtonStateParams>
 */
export function fileShareUploadButtonState(params) {
    return { evid: 881, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'fileShareUploadButtonAddOnAdded' event (evid: 900)
 *
 * Event description: file share -> upload button add-on added
 *
 * @param {fileShareUploadButtonAddOnAddedParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<fileShareUploadButtonAddOnAddedParams>} LogObject<fileShareUploadButtonAddOnAddedParams>
 */
export function fileShareUploadButtonAddOnAdded(params) {
    return { evid: 900, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sitePromoUouView' event (evid: 909)
 *
 * Event description: site promo -> uou view
 *
 * @param {sitePromoUouViewParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {string} template_type - announcement
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {string} promo_type - coupon / automatic_discount/ site_page
 * @property {GUID} visitor_id - visitor_id
 * @property {string} cta_id - cta_id
 * @property {GUID} promo_id - promo_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<sitePromoUouViewParams>} LogObject<sitePromoUouViewParams>
 */
export function sitePromoUouView(params) {
    return { evid: 909, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
/**
 * A function to create a log object for the 'sitePromoUouCtaClick' event (evid: 910)
 *
 * Event description: site promo -> uou cta click
 *
 * @param {sitePromoUouCtaClickParams} params - The parameters for this event
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} instance_id - instance_id
 * @property {string} template_type - announcement
 * @property {GUID} biToken - msid
 * @property {GUID} uuid - uuid
 * @property {string} promo_type - coupon / automatic_discount / site_page
 * @property {GUID} visitor_id - visitor_id
 * @property {string} cta_id - cta id
 * @property {GUID} promo_id - promo_id
 * @property {string} comp_id - comp_id
 * @property {GUID} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<sitePromoUouCtaClickParams>} LogObject<sitePromoUouCtaClickParams>
 */
export function sitePromoUouCtaClick(params) {
    return { evid: 910, src: 63, endpoint: 'wixlabs-ugc', params: params };
}
//# sourceMappingURL=index.js.map